import React, {
  type ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, Stack } from '@mui/material'
import {
  Calendar,
  Defaults,
  EventTypeName,
  PuiCheckbox,
  PuiTextArea,
  PuiTextField,
  useFields,
  Utils,
} from '@pbt/pbt-ui-components'

import type { ReminderProtocol } from '~/api/graphql/generated/types'
import AgeRangeSelector from '~/components/common/inputs/AgeRangeSelector'
import FeatureToggle from '~/constants/featureToggle'
import { LandingType } from '~/constants/landingConstants'
import SnapshotsAliasTypes from '~/constants/SnapshotsAliasTypes'
import { getWidgetData } from '~/store/duck/landing'
import {
  fetchReminderProtocolsByProcedureOnly,
  getReminderProtocolsByProcedureOnly,
  getReminderProtocolsIsLoading,
} from '~/store/duck/reminderProtocolsV2'
import { getMultipleReminders } from '~/store/duck/reminders'
import { getAgeUnits, getFeatureToggle } from '~/store/reducers/constants'
import { getPatient } from '~/store/reducers/patients'
import type { DataHandle, VaccineHistoryItem } from '~/types'
import { ReminderStateName } from '~/types/entities/reminders'
import { getAgeUnitIdByTimeOffsetUnit } from '~/utils/timeRangeUtils'
import { useEventType } from '~/utils/useEventType'
import useFieldsChanged from '~/utils/useFieldsChanged'

import { VaccineHistoryReminderSelect } from './VaccineHistoryReminderSelect'

export interface VaccineHistoryExpandedDetailsProps {
  edit?: boolean
  onUpdateItem?: (item: VaccineHistoryItem) => void
  order?: VaccineHistoryItem
  patientId: string | null
  vaccine: VaccineHistoryItem
}

export interface VaccineHistoryExpandedDetailsHandle
  extends DataHandle<VaccineHistoryItem> {
  updateNotes: (notes: string) => void
}

export const findMatchingReminderProtocol = (
  reminderProtocols: ReminderProtocol[],
  dueDateAmount: { value: number },
  dueDateUnitId: { value: string },
  AgeUnits: { id: string; name: string }[],
) => {
  const normalizeUnit = (str: string) =>
    str.trim().toLowerCase().replace(/s$/, '')

  const getIdFromUnitName = (name: string) => {
    const normalized = normalizeUnit(name)
    return AgeUnits.find((unit) => normalizeUnit(unit.name) === normalized)?.id
  }

  return reminderProtocols.find(
    (protocol) =>
      protocol.dueDateOffset?.amount === dueDateAmount.value &&
      getIdFromUnitName(protocol.dueDateOffset?.unit) === dueDateUnitId.value,
  )
}

export function checkDuplicateReminders(
  existingReminders?: { protocolId?: string; state?: { id: string } }[] | null,
  reminderProtocols?: { id: string }[] | null,
  activeReminderStateIds?: string[],
): boolean {
  if (!Array.isArray(existingReminders) || !Array.isArray(reminderProtocols)) {
    return false
  }
  if (existingReminders.length === 0 || reminderProtocols.length === 0) {
    return false
  }
  if (!activeReminderStateIds || activeReminderStateIds.length === 0) {
    return false
  }
  const protocolIds = new Set(reminderProtocols.map((p) => p.id))
  return existingReminders.some(
    (r) =>
      r &&
      r.protocolId &&
      protocolIds.has(r.protocolId) &&
      r.state?.id &&
      activeReminderStateIds.includes(r.state.id),
  )
}

export const VaccineHistoryExpandedDetails = forwardRef(
  function VaccineHistoryExpandedDetails(
    {
      edit,
      vaccine: vaccineProp,
      order,
      onUpdateItem,
      patientId,
    }: VaccineHistoryExpandedDetailsProps,
    ref: ForwardedRef<VaccineHistoryExpandedDetailsHandle>,
  ) {
    const AgeUnits = useSelector(getAgeUnits)
    const ReminderStates = useEventType(EventTypeName.Reminder, 'states')
    const { t } = useTranslation(['Common', 'MedicalHistory'])
    const dispatch = useDispatch()
    const patient = useSelector(getPatient(patientId))

    // TODO: remove this feature toggle after all business rollout, https://chewyinc.atlassian.net/browse/CVC-24220
    const isImprovedReminderFlowEnabled = useSelector(
      getFeatureToggle(FeatureToggle.CVC_17750_REMINDER_OVERHAUL),
    )
    const isSpeciesFilteringEnabled = useSelector(
      getFeatureToggle(FeatureToggle.REMINDER_PROTOCOL_SPECIES_FILTERING),
    )

    const vaccine = vaccineProp || order

    useEffect(() => {
      if (vaccine) {
        dispatch(
          fetchReminderProtocolsByProcedureOnly({
            procedureId: vaccine.id,
            // Only pass patient species when feature flag is enabled for backwards compatibility
            patientSpeciesId: isSpeciesFilteringEnabled
              ? patient?.species
              : undefined,
          }),
        )
      }
    }, [vaccine, patient?.species, isSpeciesFilteringEnabled, dispatch])

    const reminderProtocols = useSelector(
      getReminderProtocolsByProcedureOnly(
        vaccine?.id ?? '',
        isSpeciesFilteringEnabled ? patient?.species : undefined,
      ),
    )

    const reminderProtocolsIsLoading = useSelector(
      getReminderProtocolsIsLoading,
    )

    const remindersIds = useSelector(
      getWidgetData(
        LandingType.CLIENT_AND_PATIENT_SNAPSHOTS,
        SnapshotsAliasTypes.Reminders,
      ),
    )
    const existingReminders = useSelector(getMultipleReminders(remindersIds))

    const defaultDueDate =
      vaccine?.dueDate?.amount || Defaults.DEFAULT_DUE_DATE_AMOUNT
    const defaultDueDateUnitId =
      vaccine?.dueDate?.unitId ||
      getAgeUnitIdByTimeOffsetUnit(Defaults.DEFAULT_DUE_DATE_UNIT, AgeUnits)

    const activeReminderStateIds = [
      Utils.findConstantIdByName(ReminderStateName.OPEN, ReminderStates),
      Utils.findConstantIdByName(ReminderStateName.OVERDUE, ReminderStates),
    ]

    const hasDuplicateReminders = checkDuplicateReminders(
      existingReminders,
      reminderProtocols,
      activeReminderStateIds,
    )

    const reminderField = isImprovedReminderFlowEnabled
      ? {
          name: 'reminder',
          label: t(
            'MedicalHistory:VACCINE_HISTORY_EXPANDED_DETAILS.LABEL.CHOOSE_REMINDER',
          ),
          initialValue: undefined,
          type: 'select' as const,
        }
      : {
          name: 'addReminder',
          label: t(
            'MedicalHistory:VACCINE_HISTORY_EXPANDED_DETAILS.LABEL.ADD_REMINDER',
          ),
          type: 'toggle' as const,
          initialValue: vaccine?.addReminder || false,
        }

    const { fields, validate } = useFields(
      [
        {
          name: 'dateGiven',
          label: t('Common:DATE_GIVEN_VACCINE'),
          validators: ['required'],
          initialValue: vaccine?.dateGiven || vaccine?.date || '',
        },
        { name: 'dueDateAmount', initialValue: defaultDueDate },
        { name: 'dueDateUnitId', initialValue: defaultDueDateUnitId },
        {
          name: 'administeredIn',
          label: t(
            'MedicalHistory:VACCINE_HISTORY_EXPANDED_DETAILS.LABEL.PRACTICE_VACCINE',
          ),
          initialValue: vaccine?.administeredIn || '',
        },
        {
          name: 'notes',
          label: t('Common:NOTES'),
          initialValue: vaccine?.notes || '',
        },
        reminderField,
      ],
      false,
    )

    const {
      dateGiven,
      dueDateAmount,
      dueDateUnitId,
      addReminder,
      administeredIn,
      notes,
      reminder,
    } = fields
    const hideReminderToggle =
      (edit &&
        dueDateAmount.value === defaultDueDate &&
        dueDateUnitId.value === defaultDueDateUnitId) ||
      isImprovedReminderFlowEnabled
    const showReminderSelect = isImprovedReminderFlowEnabled

    const matchingReminderProtocol = findMatchingReminderProtocol(
      reminderProtocols,
      dueDateAmount,
      dueDateUnitId,
      AgeUnits,
    )

    const getVaccine = () => ({
      ...vaccine,
      dateGiven: dateGiven.value,
      administeredIn: administeredIn.value,
      dueDate: {
        amount: dueDateAmount.value,
        unitId: dueDateUnitId.value,
      },
      notes: notes.value,
      procedureId: edit ? vaccine.procedureId : vaccine.id,
      addReminder:
        !hasDuplicateReminders &&
        Boolean(reminder?.value || addReminder?.value),
      protocolId: reminder?.value,
    })

    useFieldsChanged(() => {
      if (onUpdateItem) {
        onUpdateItem(getVaccine())
      }
    }, fields)

    useImperativeHandle(ref, () => ({
      validate,
      updateNotes: notes.setValue,
      get: getVaccine,
    }))

    return (
      <Grid container item direction="column">
        <Stack alignItems="center" direction="row" spacing={2}>
          <Grid item>
            <Calendar field={dateGiven} label={`${dateGiven.label}*`} />
          </Grid>
          <Grid item>
            <AgeRangeSelector
              fromLabel={t('Common:EXPIRATION_DATE_IN')}
              id="due-date-select"
              label={t('Common:EXPIRATION_DATE')}
              minValue={1}
              range={false}
              startUnit={dueDateUnitId.value}
              startValue={dueDateAmount.value}
              onStartChange={dueDateAmount.setValue}
              onStartUnitChange={dueDateUnitId.setValue}
            />
          </Grid>
          {!hideReminderToggle && (
            <Grid item>
              <PuiCheckbox field={addReminder} label={addReminder.label} />
            </Grid>
          )}
          {showReminderSelect && (
            <Grid item xs={5}>
              <VaccineHistoryReminderSelect
                defaultReminderProtocolId={matchingReminderProtocol?.id}
                hasDuplicateReminders={hasDuplicateReminders}
                patientId={patientId}
                reminder={reminder}
                reminderProtocols={reminderProtocols}
                reminderProtocolsIsLoading={reminderProtocolsIsLoading}
              />
            </Grid>
          )}
        </Stack>
        <Grid item>
          <PuiTextField
            fullWidth
            field={administeredIn}
            label={administeredIn.label}
          />
        </Grid>
        {edit && (
          <Grid item>
            <PuiTextArea field={notes} placeholder={notes.label} />
          </Grid>
        )}
      </Grid>
    )
  },
)

export default VaccineHistoryExpandedDetails
